import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { coworkApi, type WikiRebuildPage, type WikiRebuildResult } from '../api/coworkApi';
import { rebuildCounts } from './wikiRebuildCounts';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * "Rebuild from Brand OS" (staff, support #122). Opening it runs a dry run, so the confirm step names every page it
 * would replace; confirming runs it for real. Cancel has the focus (Radix AlertDialog), so Enter never rebuilds by
 * accident. Which pages count as built from the Brand OS is decided by the API (cowork-api engine/wikiRebuild.ts).
 */
export function WikiRebuildDialog({ open, brandId, onClose, onDone }: {
  open: boolean;
  brandId: string;
  onClose: () => void;
  onDone: (result: WikiRebuildResult) => void;
}) {
  const [preview, setPreview] = useState<WikiRebuildResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPreview(null); setError(null); setRunning(false);
    coworkApi.wiki.rebuildFromBrandOs({ brandId, dryRun: true })
      .then((r) => { if (!cancelled) setPreview(r); })
      .catch((e) => { if (!cancelled) setError((e as Error).message || 'Could not check the wiki'); });
    return () => { cancelled = true; };
  }, [open, brandId]);

  const confirm = async () => {
    setRunning(true); setError(null);
    try {
      const r = await coworkApi.wiki.rebuildFromBrandOs({ brandId, dryRun: false });
      onDone(r);
    } catch (e) {
      setError((e as Error).message || 'Rebuild failed');
    } finally {
      setRunning(false);
    }
  };

  const counts = preview ? rebuildCounts(preview) : { replaced: 0, fresh: 0 };
  const nothingToDo = !!preview && counts.replaced === 0 && counts.fresh === 0;
  // Written over in place are listed under their old title (what goes) and their new one (what comes).
  const replacedList = preview ? [...preview.pages.overwritten.map((p) => ({ ...p, title: p.previousTitle })), ...preview.pages.removed] : [];
  const freshList = preview ? [...preview.pages.overwritten, ...preview.pages.created] : [];
  const changedSince = preview?.pages.kept.filter((k) => k.reason === 'edited' || k.reason === 'other_sources') || [];

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o && !running) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Rebuild from Brand OS?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-[13px] text-[#475467]">
              {!preview && !error && (
                <p className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Checking which pages were built from the Brand OS…</p>
              )}
              {preview && !nothingToDo && (
                <p>
                  {counts.replaced > 0
                    ? <>Replace {plural(counts.replaced, 'page')} built from the Brand OS with fresh ones from the current Brand OS.</>
                    : <>Add {plural(counts.fresh, 'page')} from the current Brand OS.</>}
                  {' '}Hand-written and template pages are kept.
                </p>
              )}
              {nothingToDo && <p>Nothing to rebuild: this wiki has no pages built from the Brand OS, and the Brand OS has nothing new to add.</p>}
              {counts.replaced > 0 && <PageList label={`Replaced (${counts.replaced})`} pages={replacedList} testId="rebuild-removed" />}
              {counts.fresh > 0 && <PageList label={`Built fresh from the current Brand OS (${counts.fresh})`} pages={freshList} testId="rebuild-created" />}
              {changedSince.length > 0 && (
                <PageList label={`Kept: built from the Brand OS but changed since (${changedSince.length})`} pages={changedSince} testId="rebuild-kept-changed" />
              )}
              {preview && preview.skipped > 0 && (
                <p className="text-[12px]">{plural(preview.skipped, 'fresh page')} not written because a kept page has the same name: {preview.pages.skipped.map((p) => p.title).join(', ')}.</p>
              )}
              {preview && counts.replaced > 0 && <p className="text-[#b42318]" style={{ fontWeight: 600 }}>The replaced pages' current text is not kept: this can't be undone.</p>}
              {error && <p role="alert" className="text-[#b42318]">{error}</p>}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={running}>{nothingToDo ? 'Close' : 'Cancel'}</AlertDialogCancel>
          {!nothingToDo && (
            <AlertDialogAction
              disabled={!preview || running}
              onClick={(e) => { e.preventDefault(); void confirm(); }}
              className="bg-[#d92d20] text-white hover:bg-[#b42318]"
            >
              {running && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />} Rebuild
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PageList({ label, pages, testId }: { label: string; pages: WikiRebuildPage[]; testId: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#98a2b3]" style={{ fontWeight: 600 }}>{label}</div>
      <ul data-testid={testId} className="mt-1 max-h-[140px] overflow-y-auto rounded-md border border-[#eaecf0] px-3 py-1.5 text-[12px] text-[#344054]">
        {pages.map((p, i) => <li key={`${p.slug}:${i}`} className="truncate py-0.5">{p.title}</li>)}
      </ul>
    </div>
  );
}
